import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import type { RootState } from '../../../store';
import {
  useAdminAtualizarHorarioMutation,
  useAdminCriarHorarioMutation,
  useAdminListarHorariosQuery,
  useAdminListarPausasQuery,
  useAdminPausarCanalMutation,
  useAdminRemoverHorarioMutation,
  type CanalCardapio,
  type HorarioFuncionamentoLoja,
} from '../../../api';
import { HeaderTela } from '../../../components/admin/header-tela';

// Ordem prática pro restaurante: começa na Segunda, termina no Domingo.
// O índice no array é `diaSemana` (0=Dom..6=Sáb).
const ORDEM_DIAS: { dia: number; nome: string }[] = [
  { dia: 1, nome: 'Segunda-feira' },
  { dia: 2, nome: 'Terça-feira' },
  { dia: 3, nome: 'Quarta-feira' },
  { dia: 4, nome: 'Quinta-feira' },
  { dia: 5, nome: 'Sexta-feira' },
  { dia: 6, nome: 'Sábado' },
  { dia: 0, nome: 'Domingo' },
];

/** Abas de horário: a BASE (canal null) + um override opcional por canal. */
type Aba = 'BASE' | CanalCardapio;
const ABAS: { valor: Aba; rotulo: string }[] = [
  { valor: 'BASE', rotulo: 'Padrão (todos)' },
  { valor: 'CARDAPIO_DELIVERY', rotulo: 'Delivery' },
  { valor: 'CARDAPIO_COMANDA_MESA', rotulo: 'Comanda / Mesa' },
  { valor: 'CARDAPIO_TOTEM', rotulo: 'Totem' },
  { valor: 'CARDAPIO_TOTEM_CHECKOUT', rotulo: 'Totem Checkout' },
  { valor: 'CARDAPIO_VITRINE', rotulo: 'Vitrine' },
];
const CANAIS_PAUSAVEIS = ABAS.filter((a) => a.valor !== 'BASE') as { valor: CanalCardapio; rotulo: string }[];

/**
 * Faixa horária no draft local. `id` ausente = faixa nova (POST).
 * `id` presente + removida do draft = DELETE.
 * `id` presente + (`horaInicio`/`horaFim`/`ativo` diferentes do original) = PATCH.
 */
interface FaixaDraft {
  /** Chave estável só pro React (faixas novas não têm id). */
  key: string;
  id?: number;
  horaInicio: string;
  horaFim: string;
  ativo: boolean;
}

interface EstadoDia {
  aberto: boolean;
  faixas: FaixaDraft[];
}

type DraftPorDia = Record<number, EstadoDia>;

function normalizarHora(h: string): string {
  // backend manda "HH:MM:SS", input type=time trabalha com "HH:MM"
  return h.slice(0, 5);
}

function diaVazio(): EstadoDia {
  return { aberto: false, faixas: [] };
}

/** Faixas do horário pertencentes a uma aba (BASE = canal null; senão o canal). */
function faixasDaAba(horarios: HorarioFuncionamentoLoja[], aba: Aba): HorarioFuncionamentoLoja[] {
  return horarios.filter((h) => (aba === 'BASE' ? h.canal == null : h.canal === aba));
}

function montarDraftInicial(horarios: HorarioFuncionamentoLoja[]): DraftPorDia {
  const draft: DraftPorDia = {};
  for (let d = 0; d < 7; d += 1) draft[d] = diaVazio();

  // Carrega TODAS as faixas (inclusive inativas): uma faixa inativa = dia fechado
  // com o horário preservado. O trash faz hard delete, então faixa inativa aqui
  // sempre significa "fechado, mas salvo".
  for (const h of horarios) {
    const estado = draft[h.diaSemana] ?? diaVazio();
    estado.faixas.push({
      key: `srv-${h.id}`,
      id: h.id,
      horaInicio: normalizarHora(h.horaInicio),
      horaFim: normalizarHora(h.horaFim),
      ativo: h.ativo,
    });
    draft[h.diaSemana] = estado;
  }

  for (const d of Object.keys(draft)) {
    const estado = draft[Number(d)]!;
    estado.faixas.sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
    // O dia está "aberto" se tem ao menos uma faixa ativa.
    estado.aberto = estado.faixas.some((f) => f.ativo);
  }
  return draft;
}

let contadorKey = 0;
function novaKey(): string {
  contadorKey += 1;
  return `novo-${contadorKey}`;
}

export function AdminHorarioPage(): JSX.Element {
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada)!;
  const { data, isLoading, isFetching } = useAdminListarHorariosQuery(idConfig);
  const [criar] = useAdminCriarHorarioMutation();
  const [atualizar] = useAdminAtualizarHorarioMutation();
  const [remover] = useAdminRemoverHorarioMutation();

  const { data: pausasResp } = useAdminListarPausasQuery(idConfig);
  const [pausarCanal, { isLoading: pausando }] = useAdminPausarCanalMutation();
  const pausas = pausasResp?.pausasCanal ?? {};

  const horarios = useMemo(() => data ?? [], [data]);

  const [abaAtiva, setAbaAtiva] = useState<Aba>('BASE');
  // Editor aberto para um canal SEM override ainda salvo (fluxo "Personalizar").
  const [editandoOverride, setEditandoOverride] = useState(false);
  const [draft, setDraft] = useState<DraftPorDia>(() => montarDraftInicial([]));
  const [salvando, setSalvando] = useState(false);
  const [erroValidacao, setErroValidacao] = useState<string | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  // Reconstrói o draft a partir das faixas da aba sempre que os dados ou a aba
  // mudam (inclui o refetch após salvar — traz os ids reais, evitando repost).
  useEffect(() => {
    setDraft(montarDraftInicial(faixasDaAba(horarios, abaAtiva)));
    setEditandoOverride(false);
  }, [horarios, abaAtiva]);

  const faixasServidorAba = faixasDaAba(horarios, abaAtiva);
  const temOverride = abaAtiva !== 'BASE' && faixasServidorAba.length > 0;
  const mostrarEditor = abaAtiva === 'BASE' || temOverride || editandoOverride;

  function trocarAba(nova: Aba): void {
    setAbaAtiva(nova);
    setErroValidacao(null);
    setErroSalvar(null);
  }

  function toggleAberto(dia: number, aberto: boolean): void {
    setDraft((d) => {
      const atual = d[dia]!;
      // Ao abrir um dia que estava fechado e sem faixas, sugere uma faixa padrão.
      if (aberto && atual.faixas.length === 0) {
        return {
          ...d,
          [dia]: {
            aberto: true,
            faixas: [{ key: novaKey(), horaInicio: '11:00', horaFim: '14:00', ativo: true }],
          },
        };
      }
      // Fechar/abrir NÃO apaga as faixas — só alterna o `ativo` de cada uma
      // (preserva os horários salvos; o save persiste como ativo=false/true).
      return {
        ...d,
        [dia]: {
          aberto,
          faixas: atual.faixas.map((f) => ({ ...f, ativo: aberto })),
        },
      };
    });
  }

  function adicionarFaixa(dia: number): void {
    setDraft((d) => ({
      ...d,
      [dia]: {
        ...d[dia]!,
        aberto: true,
        faixas: [
          ...d[dia]!.faixas,
          { key: novaKey(), horaInicio: '18:00', horaFim: '23:00', ativo: true },
        ],
      },
    }));
  }

  function removerFaixa(dia: number, key: string): void {
    setDraft((d) => ({
      ...d,
      [dia]: { ...d[dia]!, faixas: d[dia]!.faixas.filter((f) => f.key !== key) },
    }));
  }

  function alterarFaixa(dia: number, key: string, patch: Partial<FaixaDraft>): void {
    setDraft((d) => ({
      ...d,
      [dia]: {
        ...d[dia]!,
        faixas: d[dia]!.faixas.map((f) => (f.key === key ? { ...f, ...patch } : f)),
      },
    }));
  }

  /** Abre o editor do canal copiando a grade BASE como ponto de partida (faixas novas). */
  function personalizar(): void {
    const copia = montarDraftInicial(faixasDaAba(horarios, 'BASE'));
    for (const d of Object.keys(copia)) {
      const est = copia[Number(d)]!;
      est.faixas = est.faixas.map((f) => ({
        key: novaKey(),
        horaInicio: f.horaInicio,
        horaFim: f.horaFim,
        ativo: f.ativo,
      }));
    }
    setDraft(copia);
    setEditandoOverride(true);
  }

  function cancelarPersonalizacao(): void {
    setEditandoOverride(false);
    setDraft(montarDraftInicial(faixasDaAba(horarios, abaAtiva)));
  }

  /** Remove TODAS as faixas do canal — ele volta a herdar o horário padrão. */
  async function voltarAoPadrao(): Promise<void> {
    setErroSalvar(null);
    const faixas = faixasDaAba(horarios, abaAtiva);
    if (faixas.length === 0) {
      setEditandoOverride(false);
      return;
    }
    setSalvando(true);
    try {
      await Promise.all(faixas.map((h) => remover(h.id).unwrap()));
      setEditandoOverride(false);
      setSucesso(true);
    } catch {
      setErroSalvar('Não foi possível voltar ao horário padrão.');
    } finally {
      setSalvando(false);
    }
  }

  async function togglePausa(canal: CanalCardapio, pausado: boolean): Promise<void> {
    setErroSalvar(null);
    try {
      await pausarCanal({ idConfiguracaoCardapio: idConfig, canal, pausado }).unwrap();
    } catch {
      setErroSalvar('Não foi possível alterar a pausa do canal.');
    }
  }

  function validar(): string | null {
    for (const { dia, nome } of ORDEM_DIAS) {
      const estado = draft[dia]!;
      if (!estado.aberto) continue;
      for (const f of estado.faixas) {
        if (!f.horaInicio || !f.horaFim) {
          return `${nome}: preencha início e fim de todas as faixas.`;
        }
        if (f.horaInicio >= f.horaFim) {
          return `${nome}: horário de início (${f.horaInicio}) deve ser menor que o fim (${f.horaFim}).`;
        }
      }
    }
    return null;
  }

  async function salvar(): Promise<void> {
    setErroSalvar(null);
    setErroValidacao(null);

    const erro = validar();
    if (erro) {
      setErroValidacao(erro);
      return;
    }

    // Escopo do diff = SÓ as faixas desta aba (canal ou base). Assim salvar um
    // canal nunca mexe nas faixas dos outros canais nem na base.
    const canalPost: CanalCardapio | null = abaAtiva === 'BASE' ? null : abaAtiva;
    const faixasServidor = faixasDaAba(horarios, abaAtiva);
    const idsServidor = new Set(faixasServidor.map((h) => h.id));
    const draftPorId = new Map<number, FaixaDraft & { dia: number }>();
    const novos: { dia: number; faixa: FaixaDraft }[] = [];

    for (const { dia } of ORDEM_DIAS) {
      const estado = draft[dia]!;
      for (const f of estado.faixas) {
        if (f.id == null) novos.push({ dia, faixa: f });
        else draftPorId.set(f.id, { ...f, dia });
      }
    }

    const promessas: Promise<unknown>[] = [];

    // POST faixas novas — só as ativas (faixa nova num dia fechado é descartada).
    for (const { dia, faixa } of novos) {
      if (!faixa.ativo) continue;
      promessas.push(
        criar({
          idConfiguracaoCardapio: idConfig,
          canal: canalPost,
          diaSemana: dia,
          horaInicio: faixa.horaInicio,
          horaFim: faixa.horaFim,
        }).unwrap(),
      );
    }

    // PATCH faixas alteradas — inclui a troca de `ativo` (abrir/fechar o dia sem
    // apagar a faixa, preservando os horários salvos).
    for (const h of faixasServidor) {
      const atual = draftPorId.get(h.id);
      if (!atual) continue; // foi removida (trash) — trata no DELETE abaixo
      const mudouInicio = atual.horaInicio !== normalizarHora(h.horaInicio);
      const mudouFim = atual.horaFim !== normalizarHora(h.horaFim);
      const mudouDia = atual.dia !== h.diaSemana;
      const mudouAtivo = atual.ativo !== h.ativo;
      if (mudouInicio || mudouFim || mudouDia || mudouAtivo) {
        const dados: { horaInicio?: string; horaFim?: string; diaSemana?: number; ativo?: boolean } = {};
        if (mudouInicio) dados.horaInicio = atual.horaInicio;
        if (mudouFim) dados.horaFim = atual.horaFim;
        if (mudouDia) dados.diaSemana = atual.dia;
        if (mudouAtivo) dados.ativo = atual.ativo;
        promessas.push(atualizar({ id: h.id, dados }).unwrap());
      }
    }

    // DELETE (hard) só das faixas realmente removidas do draft (trash).
    for (const id of idsServidor) {
      if (!draftPorId.has(id)) {
        promessas.push(remover(id).unwrap());
      }
    }

    if (promessas.length === 0) {
      // Personalização nova sem nenhum dia aberto NÃO cria override (canal sem
      // faixa própria herda o padrão) — evita o falso "sucesso" e orienta o admin.
      if (editandoOverride && !temOverride) {
        setErroValidacao('Abra ao menos um dia para criar um horário próprio deste canal.');
        return;
      }
      setSucesso(true);
      return;
    }

    setSalvando(true);
    try {
      await Promise.all(promessas);
      setSucesso(true);
    } catch (err) {
      const msg = (err as { data?: { mensagem?: string } })?.data?.mensagem;
      setErroSalvar(msg ?? 'Falha ao salvar horários.');
    } finally {
      setSalvando(false);
    }
  }

  if (isLoading) {
    return (
      <>
        <HeaderTela
          titulo="Horários de funcionamento"
          descricao="Defina os horários da semana. Você pode ter horários diferentes por tipo de pedido."
        />
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <HeaderTela
        titulo="Horários de funcionamento"
        descricao="Defina os horários da semana. Você pode ter horários diferentes por tipo de pedido — e pausar um canal na hora."
      />

      {/* Pausa rápida por canal (liga/desliga em tempo real, sem mexer no horário). */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Pausar agora
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Interrompe um canal imediatamente, sem alterar a grade. Reative quando quiser.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {CANAIS_PAUSAVEIS.map(({ valor, rotulo }) => {
            const pausado = !!pausas[valor];
            return (
              <Button
                key={valor}
                size="small"
                variant={pausado ? 'contained' : 'outlined'}
                color={pausado ? 'warning' : 'primary'}
                startIcon={pausado ? <PlayArrowIcon /> : <PauseIcon />}
                onClick={() => togglePausa(valor, !pausado)}
                disabled={pausando}
              >
                {pausado ? `Reativar ${rotulo}` : `Pausar ${rotulo}`}
              </Button>
            );
          })}
        </Stack>
      </Paper>

      {/* Abas: horário padrão + override por canal. */}
      <Tabs
        value={abaAtiva}
        onChange={(_e, v) => trocarAba(v as Aba)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {ABAS.map((a) => (
          <Tab key={a.valor} value={a.valor} label={a.rotulo} />
        ))}
      </Tabs>

      {abaAtiva !== 'BASE' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {temOverride || editandoOverride
            ? 'Este canal tem horário próprio — ele ignora o horário padrão.'
            : 'Este canal usa o horário padrão.'}
        </Alert>
      )}

      {!mostrarEditor ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Personalize apenas se este canal funciona em horários diferentes do padrão
            (ex.: o delivery fecha mais cedo que o salão). A grade começa como uma cópia
            do horário padrão, que você ajusta.
          </Typography>
          <Button variant="contained" onClick={personalizar}>
            Personalizar horário deste canal
          </Button>
        </Paper>
      ) : (
        <>
          <Stack spacing={2}>
            {ORDEM_DIAS.map(({ dia, nome }) => {
              const estado = draft[dia] ?? diaVazio();
              return (
                <Paper key={dia} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 3 }} alignItems={{ md: 'flex-start' }}>
                    <Box sx={{ minWidth: { md: 220 } }}>
                      <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', md: '1.15rem' } }}>
                        {nome}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <Switch
                          checked={estado.aberto}
                          onChange={(e) => toggleAberto(dia, e.target.checked)}
                          disabled={salvando}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {estado.aberto ? 'Aberto' : 'Fechado'}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box sx={{ flex: 1, width: '100%' }}>
                      {!estado.aberto && estado.faixas.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Fechado neste dia
                        </Typography>
                      ) : (
                        // Fechado: mantém as faixas visíveis, porém opacas e desabilitadas
                        // (os horários continuam salvos; volta a editar ao reabrir o dia).
                        <Stack spacing={1.5} sx={{ opacity: estado.aberto ? 1 : 0.5 }}>
                          {!estado.aberto && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Fechado neste dia — horários preservados.
                            </Typography>
                          )}
                          {estado.faixas.map((faixa) => (
                            <Stack
                              key={faixa.key}
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={{ xs: 1, sm: 2 }}
                              alignItems={{ xs: 'stretch', sm: 'center' }}
                            >
                              <TextField
                                label="Início"
                                type="time"
                                value={faixa.horaInicio}
                                onChange={(e) => alterarFaixa(dia, faixa.key, { horaInicio: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 60 }}
                                size="small"
                                disabled={salvando || !estado.aberto}
                                sx={{ flex: 1, minWidth: { sm: 140 } }}
                              />
                              <TextField
                                label="Fim"
                                type="time"
                                value={faixa.horaFim}
                                onChange={(e) => alterarFaixa(dia, faixa.key, { horaFim: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 60 }}
                                size="small"
                                disabled={salvando || !estado.aberto}
                                sx={{ flex: 1, minWidth: { sm: 140 } }}
                              />
                              <IconButton
                                onClick={() => removerFaixa(dia, faixa.key)}
                                color="error"
                                disabled={salvando || !estado.aberto}
                                title="Remover faixa"
                                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Stack>
                          ))}
                          {estado.aberto && (
                            <Box>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => adicionarFaixa(dia)}
                                disabled={salvando}
                              >
                                Adicionar faixa
                              </Button>
                            </Box>
                          )}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              );
            })}

            {erroValidacao && <Alert severity="warning">{erroValidacao}</Alert>}
            {erroSalvar && <Alert severity="error">{erroSalvar}</Alert>}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ justifyContent: { sm: 'flex-end' }, pt: 1 }}
            >
              {abaAtiva !== 'BASE' && temOverride && (
                <Button color="error" onClick={voltarAoPadrao} disabled={salvando}>
                  Voltar ao horário padrão
                </Button>
              )}
              {abaAtiva !== 'BASE' && !temOverride && editandoOverride && (
                <Button onClick={cancelarPersonalizacao} disabled={salvando}>
                  Cancelar
                </Button>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={salvar}
                disabled={salvando || isFetching}
                sx={{ minWidth: { sm: 220 } }}
              >
                {salvando ? <CircularProgress size={22} color="inherit" /> : 'Salvar alterações'}
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      <Snackbar
        open={sucesso}
        autoHideDuration={3500}
        onClose={() => setSucesso(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSucesso(false)}>
          Horários atualizados com sucesso.
        </Alert>
      </Snackbar>
    </>
  );
}
